import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, X, Sparkles, ArrowRight, Info } from 'lucide-react';
import { Button } from '../ui/button';

// Precios competencia orientativos (planes básicos publicados en sus webs oficiales).
// Verificables en factorialhr.es, sesamehr.es y bizneohr.com al pie de página.
const columnas = [
  { id: 'us', nombre: 'GestionaEmpleados', destacado: true, precio: '1,25 €', unidad: '/emp/mes', nota: 'Anual · 1,50 € mensual' },
  { id: 'factorial', nombre: 'Factorial', precio: 'desde ~4,75 €', unidad: '/emp/mes', nota: 'Plan Starter' },
  { id: 'sesame', nombre: 'Sesame HR', precio: 'desde ~4 €', unidad: '/emp/mes', nota: 'Módulo tiempo' },
  { id: 'bizneo', nombre: 'Bizneo', precio: 'desde ~3 €', unidad: '/emp/mes', nota: 'Módulo control horario' },
];

// v = true | false | 'partial' | 'extra' (coste adicional) | texto libre
const filas = [
  { titulo: 'Fichaje en tablet, ordenador y móvil', us: true, factorial: true, sesame: true, bizneo: true },
  { titulo: 'GPS con geovallado por centro', us: true, factorial: true, sesame: true, bizneo: true },
  { titulo: 'Biometría (huella / Face ID) sin coste extra', us: true, factorial: 'partial', sesame: 'partial', bizneo: 'partial' },
  {
    titulo: 'Autocierre con validación del empleado (ley 2026)',
    us: true, factorial: false, sesame: false, bizneo: false, unico: true,
  },
  {
    titulo: 'Suite de 6 informes profesionales (PDF + Excel)',
    us: true, factorial: 'partial', sesame: 'partial', bizneo: 'partial', unico: true,
  },
  {
    titulo: 'Informe específico de nocturnidad (art. 36 ET)',
    us: true, factorial: false, sesame: false, bizneo: false, unico: true,
  },
  {
    titulo: 'Envío automático de informes a gestoría/RRHH',
    us: true, factorial: false, sesame: false, bizneo: false, unico: true,
  },
  { titulo: 'Cuadrante semanal con rotaciones en 1 clic', us: true, factorial: 'partial', sesame: true, bizneo: true },
  { titulo: 'Horas esperadas por día o heredadas de centro/dpto', us: true, factorial: 'partial', sesame: 'partial', bizneo: 'partial' },
  { titulo: 'Multi-centro con festivos por CCAA', us: true, factorial: true, sesame: true, bizneo: true },
  { titulo: 'Roles: admin, gestoría, auditor, colaborador', us: true, factorial: 'partial', sesame: 'partial', bizneo: 'partial' },
  { titulo: 'Panel para gestorías (acceso multi-cliente)', us: true, factorial: 'extra', sesame: 'extra', bizneo: 'extra' },
  { titulo: 'Prueba gratis sin tarjeta', us: '15 días', factorial: '14 días', sesame: '14 días', bizneo: 'a petición' },
  { titulo: 'Soporte en español por teléfono y email', us: true, factorial: true, sesame: true, bizneo: true },
];

const CellValue = ({ v, destacado }) => {
  if (v === true) {
    return (
      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${destacado ? 'bg-terracotta text-white' : 'bg-forest-100 text-forest-900'}`}>
        <Check className="w-4 h-4" strokeWidth={3} />
      </span>
    );
  }
  if (v === false) {
    return (
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-forest-100/50 text-forest-700/40">
        <X className="w-4 h-4" strokeWidth={2.5} />
      </span>
    );
  }
  if (v === 'partial') {
    return <span className="font-body-landing text-xs font-bold text-forest-700/70 whitespace-nowrap">Parcial</span>;
  }
  if (v === 'extra') {
    return <span className="font-body-landing text-xs font-bold text-terracotta whitespace-nowrap">Coste extra</span>;
  }
  return <span className="font-body-landing text-xs font-bold text-forest-900 whitespace-nowrap">{v}</span>;
};

export const CompetitorComparison = ({ onDemo }) => (
  <section id="comparativa" className="py-20 sm:py-28 bg-forest-100/40" data-testid="comparativa-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="max-w-3xl mb-14">
        <p className="font-body-landing text-terracotta font-bold text-sm tracking-[0.15em] uppercase mb-4">Comparativa</p>
        <h2 className="font-display font-black text-forest-900 text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight mb-5">
          Todo lo que ellos tienen. Más lo que solo <span className="text-terracotta">tenemos nosotros</span>.
        </h2>
        <p className="font-body-landing text-forest-700 text-base md:text-lg leading-relaxed">
          Compárate con las plataformas más conocidas del mercado. Verás que
          las funcionalidades diferenciales para cumplir la <strong>nueva ley 2026</strong> las tenemos
          nosotros, y por una fracción del precio.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.55 }}
        className="rounded-3xl border border-forest-900/10 bg-white shadow-[0_16px_50px_rgb(15,28,20,0.08)] overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead>
              <tr className="border-b border-forest-900/10">
                <th className="text-left p-4 sm:p-6 w-2/5">
                  <p className="font-body-landing text-forest-700/60 text-xs uppercase tracking-wider font-bold">Funcionalidad</p>
                </th>
                {columnas.map((c) => (
                  <th
                    key={c.id}
                    className={`text-center p-4 sm:p-6 ${c.destacado ? 'bg-forest-900 text-bone relative' : ''}`}
                    data-testid={`comparativa-col-${c.id}`}
                  >
                    {c.destacado && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-terracotta text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 shadow-lg">
                        <Sparkles className="w-3 h-3" /> Mejor opción
                      </span>
                    )}
                    <p className={`font-display font-bold text-base sm:text-lg ${c.destacado ? 'text-bone' : 'text-forest-900'}`}>
                      {c.nombre}
                    </p>
                    <p className={`font-display font-black text-2xl sm:text-3xl mt-2 ${c.destacado ? 'text-terracotta' : 'text-forest-900'}`}>
                      {c.precio}
                    </p>
                    <p className={`font-body-landing text-[11px] font-semibold ${c.destacado ? 'text-bone/70' : 'text-forest-700/60'}`}>
                      {c.unidad}
                    </p>
                    <p className={`font-body-landing text-[10px] mt-1 ${c.destacado ? 'text-bone/50' : 'text-forest-700/50'}`}>
                      {c.nota}
                    </p>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filas.map((f, i) => (
                <tr
                  key={i}
                  className={`border-b border-forest-900/5 ${f.unico ? 'bg-terracotta/[0.04]' : (i % 2 === 0 ? '' : 'bg-forest-100/25')}`}
                  data-testid={`comparativa-row-${i}`}
                >
                  <td className="p-4 sm:p-5">
                    <div className="flex items-center gap-2">
                      {f.unico && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-terracotta text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 shrink-0">
                          Solo aquí
                        </span>
                      )}
                      <span className="font-body-landing text-sm text-forest-900 font-semibold leading-snug">{f.titulo}</span>
                    </div>
                  </td>
                  <td className="p-4 sm:p-5 text-center bg-forest-900/[0.02]">
                    <CellValue v={f.us} destacado />
                  </td>
                  <td className="p-4 sm:p-5 text-center"><CellValue v={f.factorial} /></td>
                  <td className="p-4 sm:p-5 text-center"><CellValue v={f.sesame} /></td>
                  <td className="p-4 sm:p-5 text-center"><CellValue v={f.bizneo} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-forest-900 noise-overlay p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-bone">
            <p className="font-display font-bold text-lg sm:text-xl mb-1">
              Cambia hoy. Ahorra desde el primer mes.
            </p>
            <p className="font-body-landing text-bone/70 text-sm">
              Para 20 empleados/año: ahorras hasta <strong className="text-terracotta">840&nbsp;€</strong> frente a Factorial.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Button asChild size="lg" className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white font-body-landing font-bold h-12 px-8 group">
              <Link to="/registro" data-testid="comparativa-trial-btn">
                Empieza gratis 15 días
                <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" onClick={onDemo} className="rounded-full border-bone/25 text-bone hover:bg-bone/10 hover:text-bone font-body-landing font-bold h-12 px-8 bg-transparent" data-testid="comparativa-demo-btn">
              Ver demo
            </Button>
          </div>
        </div>
      </motion.div>

      <div className="mt-6 flex items-start gap-2 max-w-3xl">
        <Info className="w-4 h-4 text-forest-700/60 shrink-0 mt-0.5" />
        <p className="font-body-landing text-xs text-forest-700/70 leading-relaxed">
          Comparativa orientativa basada en información pública de los planes básicos de Factorial, Sesame HR y Bizneo
          (consultada en 2026). Los precios y funcionalidades de la competencia pueden variar según el plan contratado y las promociones vigentes.
          &quot;Parcial&quot; = disponible con limitaciones o solo en planes superiores. &quot;Coste extra&quot; = requiere módulo/servicio adicional de pago.
        </p>
      </div>
    </div>
  </section>
);
